// The link between a seed and an Audiotool project, written ON the project.
//
// Seedbed stores nothing about Audiotool: the producer's own Audiotool account
// is where a seed's projects live, so that is where the link lives too. Every
// project a seed is opened into, or imported from, carries:
//
//   • a TAG naming the song — the ROOT seed — so any version of it finds every
//     project it has been through (`linkTag`);
//   • one DESCRIPTION line naming the latest seed that project was synced with
//     (`withLink`), so an import can say "last synced with v3".
//
// Tags are best-effort — Audiotool may refuse a tag it doesn't like — so the
// description line carries the root id too, and `parseLink` reads either.
// Pure: runs in the extension, the browser and the worker tests alike.

/** Where a seed page lives when the caller has no origin of its own (the extension). */
export const WEB_APP_ORIGIN = "https://app.seed-bed.com";

const TAG_PREFIX = "seedbed-";
/** `Seedbed seed <seedId> (song <rootId>): <url>` — one line, rewritten in place. */
const LINE = /^Seedbed seed (\S+) \(song (\S+)\):.*$/m;

export interface SeedLink {
  /** The song: every version of it shares this. */
  rootSeedId: string;
  /** The seed this project was last exported from or imported into. */
  seedId: string;
}

export function linkTag(rootSeedId: string): string {
  return `${TAG_PREFIX}${rootSeedId}`;
}

export function seedPageUrl(seedId: string, origin: string = WEB_APP_ORIGIN): string {
  return `${origin.replace(/\/+$/, "")}/s/${encodeURIComponent(seedId)}`;
}

/**
 * `description` with its Seedbed line set to `link` — replaced where there is
 * one, appended where there isn't. Everything else the producer wrote stays.
 */
export function withLink(description: string, link: SeedLink, origin: string = WEB_APP_ORIGIN): string {
  const line = `Seedbed seed ${link.seedId} (song ${link.rootSeedId}): ${seedPageUrl(link.seedId, origin)}`;
  if (LINE.test(description)) return description.replace(LINE, line);
  const rest = description.replace(/\s+$/, "");
  return rest === "" ? line : `${rest}\n\n${line}`;
}

/** `tags` with the song's tag added once. */
export function withLinkTag(tags: readonly string[], rootSeedId: string): string[] {
  const tag = linkTag(rootSeedId);
  return tags.includes(tag) ? [...tags] : [...tags, tag];
}

/**
 * The seed a project is linked to, from its description line — or, with no
 * line, the song alone from its tag (the seed is then the root). Null when the
 * project was never Seedbed's.
 */
export function parseLink(project: { description: string; tags: readonly string[] }): SeedLink | null {
  const m = LINE.exec(project.description);
  if (m) return { seedId: m[1]!, rootSeedId: m[2]! };
  const tag = project.tags.find((t) => t.startsWith(TAG_PREFIX) && t.length > TAG_PREFIX.length);
  if (!tag) return null;
  const rootSeedId = tag.slice(TAG_PREFIX.length);
  return { rootSeedId, seedId: rootSeedId };
}

/** Whether `project` belongs to the song `rootSeedId`. */
export function isLinkedTo(project: { description: string; tags: readonly string[] }, rootSeedId: string): boolean {
  return project.tags.includes(linkTag(rootSeedId)) || parseLink(project)?.rootSeedId === rootSeedId;
}

/**
 * The project name behind whatever a producer pasted: a studio URL
 * (`…/studio?project=<id>`), a bare id, or `projects/<id>`. Null when there is
 * no id in it at all.
 */
export function projectNameFromUrl(input: string): string | null {
  const text = input.trim();
  if (text === "") return null;
  let id = text;
  try {
    const found = new URL(text).searchParams.get("project");
    if (found) id = found;
    else return null; // a URL, but not one naming a project
  } catch {
    /* not a URL: treat it as an id */
  }
  id = id.replace(/^projects\//, "").trim();
  return id === "" ? null : `projects/${id}`;
}

export function studioUrl(projectName: string): string {
  return `https://beta.audiotool.com/studio?project=${projectName.replace(/^projects\//, "")}`;
}
